import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken: null // Explicitly bypass captcha for admin login
        }
      });
      
      setLoading(false);
      
      // If captcha error, provide clearer message
      if (error?.message?.includes('captcha')) {
        return { 
          data: null, 
          error: { 
            message: 'Please try again in a few moments or contact support if the issue persists.' 
          } 
        };
      }
      
      return { data, error };
    } catch (err) {
      setLoading(false);
      return { 
        data: null, 
        error: { message: 'Authentication failed. Please check your credentials.' } 
      };
    }
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/admin`
      }
    });
    return { data, error };
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const isAdmin = () => {
    const envEmails = (import.meta.env.VITE_ADMIN_EMAILS || '')
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    const defaultEmails = ['wayne@paradise-performance.com', 'jbrous96@gmail.com'];
    const adminEmails = envEmails.length ? envEmails : defaultEmails;
    const email = user?.email?.toLowerCase();
    return Boolean(
      (email && adminEmails.includes(email)) ||
      user?.user_metadata?.role === 'admin' ||
      user?.app_metadata?.role === 'admin'
    );
  };

  return {
    user,
    loading,
    signIn,
    signInWithGoogle,
    resetPassword,
    signOut,
    isAdmin,
  };
};